/**
 * Bundled-in fallback copy of the marketing-pages CMS defaults
 * (FEAT-SAAS-MKT-004). Mirrors
 * `packages/shared/src/api/content-defaults.ts` 1:1; we keep two
 * copies because the marketing SPA's Vite bundle can't statically
 * analyze CommonJS re-exports from the shared package for value
 * imports (types are fine). The backend reads the canonical copy
 * from @opinix-cloud/shared; if you edit one of these files, edit
 * the other too — drift = visual regressions on the marketing site.
 *
 * The marketing SPA reads this when:
 *   - localStorage cache is cold AND the API call has not landed yet
 *     (first paint).
 *   - The /api/content/:surface fetch fails (API down, offline
 *     preview).
 */

import type {
  ContentBlockType,
  ContentSurface,
  CtaBlockPayload,
  FaqBlockPayload,
  FeatureGridBlockPayload,
  HeroBlockPayload,
  LogoStripBlockPayload,
  PricingTableBlockPayload,
  RichTextBlockPayload,
} from '@opinix-cloud/shared';

export interface DefaultBlock {
  key: string;
  blockType: ContentBlockType;
  payload: unknown;
}

export interface DefaultSurface {
  surface: ContentSurface;
  blocks: DefaultBlock[];
}

// ---------- /  marketing_landing  ----------

const heroPayload: HeroBlockPayload = {
  badge: 'Built for public safety',
  headline: 'The simulation platform for the people who respond first.',
  headlineAccent: 'respond first',
  subhead:
    'Opinix lets emergency-response teams design, run, and debrief realistic multi-channel exercises — from school lockdowns to mass-casualty events to corporate continuity drills. One platform. Every scenario.',
  primaryCtaLabel: 'Request a demo',
  primaryCtaHref: null,
  secondaryCtaLabel: 'Get early access',
  secondaryCtaHref: '#early-access',
  footnote: 'Standalone appliance · cloud-managed · cloud-only.',
};

const trustStripPayload: LogoStripBlockPayload = {
  caption: 'Designed for the agencies who train for the worst day',
  items: [
    'Law enforcement',
    'K-12 & higher ed',
    'Fire & EMS',
    'Healthcare',
    'Corporate security',
    'Emergency management',
  ],
};

const platformPayload: FeatureGridBlockPayload = {
  eyebrow: 'The platform',
  heading: 'Build. Run. Review.',
  blurb:
    'One platform that covers the entire training lifecycle — from scenario design through live execution to evidence-based debrief.',
  items: [
    {
      title: 'Build',
      body:
        'Design scenarios with reusable injects, role-play personas, and multi-dashboard layouts. Version every scenario and share across your library.\n• Drag-and-drop scenario builder\n• Reusable inject library\n• Persona scripting',
      iconKey: '01',
    },
    {
      title: 'Run',
      body:
        'Coordinate teams across radios, phones, and live displays. Time-synced injects keep every player in scenario, every operator on script.\n• Multi-channel comms\n• Synchronized display nodes\n• Live operator console',
      iconKey: '02',
    },
    {
      title: 'Review',
      body:
        'Synchronized AAR playback shows exactly what happened, when, and to whom. Every debrief becomes evidence-based.\n• Synced replay across channels\n• Decision timeline\n• Exportable AAR reports',
      iconKey: '03',
    },
  ],
};

const verticalsPayload: FeatureGridBlockPayload = {
  eyebrow: 'Built for every responder',
  heading: 'One platform. Every scenario.',
  blurb:
    'Opinix flexes across verticals because every agency’s scenarios are different — but the muscle they need to build is the same.',
  items: [
    {
      title: 'Law enforcement',
      body:
        'Active-threat response, hostage negotiation, multi-agency coordination, and tactical decision-making under realistic comms load.',
      iconKey: 'shield',
    },
    {
      title: 'K-12 & higher ed',
      body:
        'Lockdown drills, evacuation routing, parent-reunification, and table-top exercises that translate to real building behavior.',
      iconKey: 'school',
    },
    {
      title: 'Fire & EMS',
      body:
        'Mass-casualty triage, hazmat response, structure-fire command, and inter-agency comms drills with synchronized timelines.',
      iconKey: 'flame',
    },
    {
      title: 'Healthcare',
      body:
        'Hospital surge, code-team coordination, evacuation, active-threat-in-facility, and continuity-of-operations exercises.',
      iconKey: 'cross',
    },
    {
      title: 'Corporate security',
      body:
        'Workplace-violence response, executive protection, business continuity, and cyber-physical incident tabletops.',
      iconKey: 'building',
    },
    {
      title: 'Emergency management',
      body:
        'EOC activations, severe-weather response, multi-jurisdictional coordination, and full-scale FEMA-style exercises.',
      iconKey: 'globe',
    },
  ],
};

const featuresPayload: FeatureGridBlockPayload = {
  eyebrow: 'What\'s inside',
  heading: 'Every tool you need to make training feel real.',
  groups: [
    {
      eyebrow: 'Comms',
      heading: 'Real comms behaviors, not pretend ones',
      blurb:
        'Trainees use the same channels they use on shift — half-duplex PTT radio, full-duplex phone, dispatch console with personas. The platform records every transmission with timestamps for replay.',
      iconKey: 'comms',
      items: [
        { title: 'Half-duplex PTT radio with channel separation', body: '' },
        { title: 'Full-duplex phone calls between roles', body: '' },
        { title: 'Dispatch console with scriptable personas', body: '' },
        { title: 'Lossless recording for AAR', body: '' },
      ],
    },
    {
      eyebrow: 'Scenarios',
      heading: 'Multi-dashboard scenes, in sync',
      blurb:
        'Display nodes and video walls run synchronized scenes — CCTV feeds, mapping, intel, status boards. Operators see what their role needs; instructors see the full picture.',
      iconKey: 'dashboard',
      items: [
        { title: 'Display Node Agent for any screen', body: '' },
        { title: 'Per-role layouts and content', body: '' },
        { title: 'Time-synced playback across nodes', body: '' },
        { title: 'Live overrides during exercise', body: '' },
      ],
    },
    {
      eyebrow: 'Library',
      heading: 'Scenarios you keep, scenarios you grow',
      blurb:
        'Start from prebuilt templates — active threat, mass casualty, severe weather, workplace violence — or compose your own with reusable injects, props, and persona scripts. Versioned, shared, audited.',
      iconKey: 'library',
      items: [
        { title: 'Prebuilt scenario templates', body: '' },
        { title: 'Reusable inject and persona library', body: '' },
        { title: 'Versioning and change history', body: '' },
        { title: 'Tenant-scoped sharing controls', body: '' },
      ],
    },
    {
      eyebrow: 'After-action review',
      heading: 'Time-locked replay. Better debriefs.',
      blurb:
        'Every comms transmission, dashboard event, and instructor inject lines up on a single timeline. Pause, scrub, annotate. Walk teams through the moment of decision — not the memory of it.',
      iconKey: 'aar',
      items: [
        { title: 'Synchronized multi-track playback', body: '' },
        { title: 'Decision timeline view', body: '' },
        { title: 'Annotations and bookmarks', body: '' },
        { title: 'Exportable AAR reports', body: '' },
      ],
    },
  ],
};

const capabilitiesPayload: FeatureGridBlockPayload = {
  eyebrow: 'Capabilities',
  heading: 'Everything inside the platform.',
  blurb:
    'Opinix is not a thin training app. It’s a full multi-channel simulation stack — comms, scenarios, execution, review, and the tenant infrastructure to run it across your whole agency.',
  groups: [
    {
      eyebrow: 'Communications',
      heading: 'Real radios. Real phones. Real radio behaviors.',
      blurb:
        'Trainees work the same comms channels they use on shift. The platform records every transmission with timestamps for replay.',
      iconKey: 'antenna',
      items: [
        {
          title: 'Half-duplex PTT radio',
          body:
            'Hold-to-talk with realistic collision handling, channel separation, and per-channel monitoring.',
        },
        {
          title: 'Full-duplex phone calls',
          body:
            'Dial / answer / hangup, ringing, hold, and per-call rooms. Tap-to-dial directory across units, personas, and personnel.',
        },
        {
          title: 'Multi-channel monitoring',
          body:
            'Subscribe and unsubscribe from channels on the fly. Spacebar PTT and instant channel switching.',
        },
        {
          title: 'Dispatch console',
          body:
            'Multi-channel monitor, persona switching, and a soundboard of pre-recorded dispatcher cues.',
        },
        {
          title: 'Live PTT transcription',
          body:
            'Browser-side speech-to-text on every transmission, persisted for instructor monitoring and AAR search.',
        },
        {
          title: 'Radio text + photo attachments',
          body:
            'Dispatch sends written notes (addresses, frequencies, intel) with up to 5 MB photos, bidirectional.',
        },
      ],
    },
    {
      eyebrow: 'Scenario authoring',
      heading: 'A scenario builder non-engineers actually use.',
      blurb:
        'Build a complete multi-channel exercise in five minutes with a guided wizard. Reusable parts you mix and match.',
      iconKey: 'scenario',
      items: [
        {
          title: '7-step authoring wizard',
          body:
            'Channels, roles, personas, injects, dispatch console, validation, finish. End-to-end in five minutes.',
        },
        {
          title: 'Reusable inject library',
          body:
            'Radio transmissions, phone calls, social-media posts, ambient noise, manual prompts. Drop them in and reuse.',
        },
        {
          title: 'Scheduled and manual triggers',
          body:
            'Time-based or instructor-fired. Mix scripted timeline events with on-the-fly injects from the dispatch console.',
        },
        {
          title: 'Persona scripting',
          body:
            'Pre-loaded personas (suspect, witness, distraught caller) with voice presets and scripted reactions.',
        },
        {
          title: 'Versioned scenario library',
          body:
            'Every save is a version. Roll back, branch, or share with other tenants in your fleet.',
        },
        {
          title: '.opx import / export',
          body:
            'Portable scenario format. Hand-off scenarios between agencies, archive for audit, or load into the simulator.',
        },
      ],
    },
    {
      eyebrow: 'Live execution',
      heading: 'Coordinate teams across radios, phones, and screens.',
      blurb:
        'Time-synced injects, live participant presence, and instructor-side overrides keep every player in scenario.',
      iconKey: 'sync',
      items: [
        {
          title: 'Multi-display orchestration',
          body:
            'Independent scenarios per display node. Each operator screen shows what their role needs; instructors see the full picture.',
        },
        {
          title: 'Time-synced across nodes',
          body:
            'Server-authoritative sim clock keeps every dashboard, video wall, and operator within ≤200ms typical / ≤1s p99.',
        },
        {
          title: 'Live participant presence',
          body:
            'Green / amber / gray status dots show who is in scenario, who is lagging, and who has dropped.',
        },
        {
          title: 'Open join codes',
          body:
            'Ad-hoc participant join with auto-allocated phone numbers and one-click admin add-to-session.',
        },
        {
          title: 'Agency-filtered dashboards',
          body:
            'Login-gated, scoped views per agency, unit, or role. Show the right people only what they need to see.',
        },
        {
          title: 'Split dispatcher views',
          body:
            'Dedicated radio operator and 911 phone operator screens, each tuned for the workload they actually run.',
        },
      ],
    },
    {
      eyebrow: 'After-action review',
      heading: 'Time-locked replay. Better debriefs.',
      blurb:
        'Every transmission, every dashboard event, every instructor inject — on a single timeline. Pause, scrub, annotate.',
      iconKey: 'clock',
      items: [
        {
          title: 'Synchronized multi-track playback',
          body:
            'Per-channel composites and per-participant tracks line up on a single scrubable timeline.',
        },
        {
          title: 'Decision-point bookmarks',
          body:
            'Mark the moment of decision and replay it cold. Walk teams through what happened, not the memory of it.',
        },
        {
          title: 'Lossless recording pipeline',
          body:
            'LiveKit Egress with Opus codec and metadata sidecars — every channel captured at session-archive quality.',
        },
        {
          title: 'Exportable AAR reports',
          body:
            'Zip archive of audio, transcripts, timeline, and decision annotations. Ready for evaluator handoff.',
        },
      ],
    },
    {
      eyebrow: 'Platform',
      heading: 'Built for agencies, not just users.',
      blurb:
        'Multi-tenant from day one, audit-logged on every action, deployable on hardware you control or in the cloud.',
      iconKey: 'shield-2',
      items: [
        {
          title: 'Multi-tenant from day one',
          body:
            'Every record is tenant-scoped. Run multiple agencies on a single deployment with hard data separation.',
        },
        {
          title: 'Role-based access control',
          body:
            'OWNER, AUTHOR, OPERATOR, EVALUATOR, MEMBER. Authoring, execution, and review separate by design.',
        },
        {
          title: 'Audit log on every action',
          body:
            'Non-blocking audit writes capture who did what, when, and to which resource. Exportable, queryable.',
        },
        {
          title: 'Display Node Agent',
          body:
            'Pi appliance image (40s boot to ready), plus desktop builds for Linux, Windows, Mac, and Android TV.',
        },
        {
          title: 'Offline tile caching',
          body:
            'Maps work without internet. Esri / OSM / CARTO tiles cached locally with CDN fallback.',
        },
        {
          title: 'Smoke-tested on every release',
          body:
            'Phase 1 dry-run suite (12 steps, 60-second runtime) gates every deploy. No surprises in the field.',
        },
      ],
    },
  ],
};

const deploymentPayload: FeatureGridBlockPayload = {
  eyebrow: 'Deployment',
  heading: 'Three ways to run Opinix.',
  blurb:
    'Pick the deployment that matches your agency’s constraints — data residency, network policy, hardware budget. Switch between them as you scale.',
  items: [
    {
      title: 'Standalone Appliance',
      body:
        '**Self-hosted. Air-gap-ready.\nRun the entire Opinix stack on a single appliance — no internet required. For agencies with strict data residency or air-gapped exercises.\n• On-premise hardware\n• No external dependencies\n• Full feature set\n• Local-only audit trail',
      iconKey: 'standalone',
    },
    {
      title: 'Cloud-Managed',
      body:
        '**Your appliance. Our control plane.\nKeep the appliance for the heavy lifting; let the cloud manage scenario libraries, team rosters, and multi-site coordination from a single pane.\n• Centralized scenario library\n• Multi-site coordination\n• Cloud-side audit + reporting\n• Bring-your-own appliance',
      iconKey: 'cloud-managed',
    },
    {
      title: 'Cloud-Only',
      body:
        '**Browser-only. No hardware.\nPair the cloud with our Display Node Agent on any screen — Pi, Linux, Windows, Mac, Android TV. Run training from anywhere.\n• Display Node Agent for any screen\n• No site appliance required\n• Pair displays in 30 seconds\n• Works over public internet',
      iconKey: 'cloud-only',
    },
  ],
};

const finalCtaPayload: CtaBlockPayload = {
  eyebrow: 'Get started',
  heading: 'Train like it’s real.',
  blurb:
    'Two ways in: book a live walkthrough, or get on the early-access list and we’ll reach out as soon as your vertical opens.',
  primaryCtaLabel: 'Request a demo',
  primaryCtaFootnote: 'Live walkthrough · 30 minutes · no commitment',
  secondaryCard: {
    heading: 'Get early access',
    blurb:
      'We’re onboarding agencies vertical by vertical. Drop your email and we’ll let you know when yours opens.',
  },
};

const footerPayload: RichTextBlockPayload = {
  body: 'Simulation and training built for the people who respond first.',
};

// ---------- /pricing ----------

const pricingHeroPayload: HeroBlockPayload = {
  badge: 'Pricing',
  headline: 'Pick a plan that fits your team.',
  headlineAccent: 'team',
  subhead:
    'Start on a single appliance with Starter. Scale to multi-site agencies and federal deployments. Same platform, same scenarios — the limits and support tier flex with you.',
  primaryCtaLabel: '',
  primaryCtaHref: null,
  secondaryCtaLabel: null,
  secondaryCtaHref: null,
  footnote: null,
};

const pricingTablePayload: PricingTableBlockPayload = {
  heading: 'Plans',
  footnote:
    'Prices in USD. Annual billing saves two months. Switch or cancel any time from your tenant settings.',
};

const pricingFaqPayload: FaqBlockPayload = {
  eyebrow: 'FAQ',
  heading: 'Common questions.',
  items: [
    {
      q: 'Can I switch plans?',
      a: 'Yes. Upgrade or downgrade any time from your tenant settings. Changes take effect at the start of your next billing period; usage limits adjust immediately on upgrade.',
    },
    {
      q: 'How does billing work?',
      a: 'Self-serve plans (Free, Starter, Pro) are billed monthly or annually by credit card. Annual billing saves you two months. Enterprise plans are quoted with custom terms and can be billed annually by invoice or PO.',
    },
    {
      q: "What's an appliance?",
      a: 'An Opinix appliance is the on-prem (or cloud-hosted) edge device that runs the simulation comms layer for an exercise. Each physical training site or simultaneous large-scale drill typically maps to one appliance — three appliances on Starter is enough for most three-site agencies.',
    },
    {
      q: 'What about air-gapped deployments?',
      a: 'Available on Enterprise. We deliver Opinix as a self-contained image you run inside your own network — no inbound or outbound connectivity to our cloud required. Updates and scenario packs ship as signed bundles you side-load on your schedule.',
    },
  ],
};

const pricingFinalCtaPayload: CtaBlockPayload = {
  eyebrow: 'Still deciding?',
  heading: 'Talk to a human.',
  blurb:
    'Every agency’s drill cadence is different. Book a 30-minute walkthrough and we’ll help you map your training plan to the right tier — no commitment.',
  primaryCtaLabel: 'Request a demo',
  primaryCtaFootnote: 'Live walkthrough · 30 minutes · no commitment',
  secondaryCard: {
    heading: 'Or get on the early-access list',
    blurb:
      'We’re onboarding agencies vertical by vertical. Drop your email and we’ll let you know when yours opens.',
  },
};

// ---------- export ----------

export const DEFAULT_SURFACES: DefaultSurface[] = [
  {
    surface: 'marketing_landing',
    blocks: [
      { key: 'hero', blockType: 'hero', payload: heroPayload },
      { key: 'trust_strip', blockType: 'logo_strip', payload: trustStripPayload },
      { key: 'platform', blockType: 'feature_grid', payload: platformPayload },
      { key: 'verticals', blockType: 'feature_grid', payload: verticalsPayload },
      { key: 'features', blockType: 'feature_grid', payload: featuresPayload },
      { key: 'capabilities', blockType: 'feature_grid', payload: capabilitiesPayload },
      { key: 'deployment', blockType: 'feature_grid', payload: deploymentPayload },
      { key: 'final_cta', blockType: 'cta', payload: finalCtaPayload },
      { key: 'footer', blockType: 'rich_text', payload: footerPayload },
    ],
  },
  {
    surface: 'pricing_page',
    blocks: [
      { key: 'hero', blockType: 'hero', payload: pricingHeroPayload },
      {
        key: 'pricing_table',
        blockType: 'pricing_table',
        payload: pricingTablePayload,
      },
      { key: 'faq', blockType: 'faq', payload: pricingFaqPayload },
      { key: 'final_cta', blockType: 'cta', payload: pricingFinalCtaPayload },
    ],
  },
  {
    surface: 'verticals_page',
    blocks: [
      // Reserved surface — not yet rendered as a standalone /verticals
      // route. The Verticals section on the landing page reads from
      // marketing_landing.verticals; this surface stays empty until a
      // dedicated vertical page lands.
    ],
  },
];

export function getDefaultSurface(
  surface: ContentSurface,
): DefaultSurface | undefined {
  return DEFAULT_SURFACES.find((s) => s.surface === surface);
}
